import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Search, Bug, AlertTriangle, CheckCircle, Clock, User, BarChart3, PieChart, TrendingDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BugReportsPage = () => {
  const { api, hasRole, user } = useAuth();
  const [bugs, setBugs] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBug, setSelectedBug] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Other',
    severity: 'Medium',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: '',
    page_url: window.location.href,
    browser_info: navigator.userAgent,
  });

  const [updateData, setUpdateData] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    resolution_notes: '',
    estimated_hours: '',
  });

  const categories = ['UI', 'Backend', 'Performance', 'Data', 'Other'];
  const severities = ['Low', 'Medium', 'High', 'Critical'];
  const statuses = ['Open', 'InProgress', 'Resolved', 'Closed', 'WontFix'];
  const priorities = ['P0', 'P1', 'P2', 'P3'];

  const fetchData = async () => {
    try {
      const [bugsRes, usersRes] = await Promise.all([
        api().get('/bugs'),
        hasRole(['Admin']) ? api().get('/users') : Promise.resolve({ data: [] })
      ]);
      setBugs(bugsRes.data);
      setUsers(usersRes.data);

      if (hasRole(['Admin'])) {
        const statsRes = await api().get('/bugs/stats/summary');
        setStats(statsRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api().post('/bugs', formData);
      toast.success('Bug report submitted');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bug report');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedBug) return;
    try {
      const payload = {};
      if (updateData.status) payload.status = updateData.status;
      if (updateData.priority) payload.priority = updateData.priority;
      if (updateData.assigned_to) payload.assigned_to = updateData.assigned_to;
      if (updateData.resolution_notes) payload.resolution_notes = updateData.resolution_notes;
      if (updateData.estimated_hours) payload.estimated_hours = parseFloat(updateData.estimated_hours);

      await api().put(`/bugs/${selectedBug.id}`, payload);
      toast.success('Bug report updated');
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bug report');
    }
  };

  const openDetail = (bug) => {
    setSelectedBug(bug);
    setUpdateData({
      status: bug.status,
      priority: bug.priority,
      assigned_to: bug.assigned_to || '',
      resolution_notes: bug.resolution_notes || '',
      estimated_hours: bug.estimated_hours?.toString() || '',
    });
    setDetailDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'Other',
      severity: 'Medium',
      steps_to_reproduce: '',
      expected_behavior: '',
      actual_behavior: '',
      page_url: window.location.href,
      browser_info: navigator.userAgent,
    });
  };

  const filteredBugs = bugs.filter(bug => {
    const matchesSearch = bug.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bug.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || bug.status === filterStatus;
    const matchesTab = activeTab === 'all' || 
      (activeTab === 'my' && bug.reported_by === user?.id) ||
      (activeTab === 'assigned' && bug.assigned_to === user?.id);
    return matchesSearch && matchesStatus && matchesTab;
  });

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-red-100 text-red-700';
      case 'High': return 'bg-orange-100 text-orange-700';
      case 'Medium': return 'bg-yellow-100 text-yellow-700';
      case 'Low': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-blue-100 text-blue-700';
      case 'InProgress': return 'bg-purple-100 text-purple-700';
      case 'Resolved': return 'bg-green-100 text-green-700';
      case 'Closed': return 'bg-slate-100 text-slate-700';
      case 'WontFix': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getUserName = (userId) => users.find(u => u.id === userId)?.full_name || '-';

  return (
    <div className="space-y-6 animate-slideIn" data-testid="bug-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bug Reports</h1>
          <p className="text-slate-500 text-sm">Report and track system issues</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="report-bug-btn">
              <Plus className="w-4 h-4 mr-2" />
              Report Bug
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-red-600" />
                Report a Bug
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Brief description of the issue"
                  required
                  className="rounded-sm"
                  data-testid="bug-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="bug-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Severity</Label>
                  <Select value={formData.severity} onValueChange={(v) => setFormData({ ...formData, severity: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="bug-severity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {severities.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of the bug..."
                  required
                  className="rounded-sm min-h-[100px]"
                  data-testid="bug-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Steps to Reproduce</Label>
                <Textarea
                  value={formData.steps_to_reproduce}
                  onChange={(e) => setFormData({ ...formData, steps_to_reproduce: e.target.value })}
                  placeholder="1. Go to...\n2. Click on...\n3. See error..."
                  className="rounded-sm"
                  data-testid="bug-steps-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Expected Behavior</Label>
                  <Textarea
                    value={formData.expected_behavior}
                    onChange={(e) => setFormData({ ...formData, expected_behavior: e.target.value })}
                    placeholder="What should happen?"
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Actual Behavior</Label>
                  <Textarea
                    value={formData.actual_behavior}
                    onChange={(e) => setFormData({ ...formData, actual_behavior: e.target.value })}
                    placeholder="What actually happens?"
                    className="rounded-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="bug-submit-btn">
                  <Bug className="w-4 h-4 mr-2" />
                  Submit Bug Report
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards (Admin Only) */}
      {hasRole(['Admin']) && stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="industrial-card">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-900 mono">{stats.total}</p>
                <p className="text-xs text-slate-500 uppercase">Total Bugs</p>
              </CardContent>
            </Card>
            <Card className="industrial-card">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600 mono">{stats.open}</p>
                <p className="text-xs text-slate-500 uppercase">Open</p>
              </CardContent>
            </Card>
            <Card className="industrial-card">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-purple-600 mono">{stats.in_progress}</p>
                <p className="text-xs text-slate-500 uppercase">In Progress</p>
              </CardContent>
            </Card>
            <Card className="industrial-card">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600 mono">{stats.resolved}</p>
                <p className="text-xs text-slate-500 uppercase">Resolved</p>
              </CardContent>
            </Card>
            <Card className="industrial-card">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600 mono">{stats.critical_open}</p>
                <p className="text-xs text-slate-500 uppercase">Critical Open</p>
              </CardContent>
            </Card>
          </div>

          {/* Bug Analysis Section */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Category Distribution */}
            <Card className="industrial-card">
              <CardHeader className="border-b border-slate-100 pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-blue-600" />
                  Bugs by Category
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {stats.by_category?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.by_category.map((cat, idx) => {
                      const percentage = Math.round((cat.count / stats.total) * 100);
                      const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-red-500'];
                      return (
                        <div key={cat._id || idx} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">{cat._id || 'Unknown'}</span>
                            <span className="mono text-slate-500">{cat.count} ({percentage}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${colors[idx % colors.length]} rounded-full transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No category data available</p>
                )}
              </CardContent>
            </Card>

            {/* Resolution Rate */}
            <Card className="industrial-card">
              <CardHeader className="border-b border-slate-100 pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                  Resolution Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-sm text-center">
                    <p className="text-3xl font-bold text-green-600 mono">
                      {stats.total > 0 ? Math.round((stats.resolved / stats.total) * 100) : 0}%
                    </p>
                    <p className="text-xs text-green-700 uppercase mt-1">Resolution Rate</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-sm text-center">
                    <p className="text-3xl font-bold text-blue-600 mono">
                      {stats.open + stats.in_progress}
                    </p>
                    <p className="text-xs text-blue-700 uppercase mt-1">Pending Issues</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-sm text-center">
                    <TrendingDown className="w-6 h-6 mx-auto text-orange-600 mb-1" />
                    <p className="text-sm font-semibold text-orange-700">
                      {stats.critical_open > 0 ? 'Action Needed' : 'Healthy'}
                    </p>
                    <p className="text-xs text-orange-600">{stats.critical_open} critical bugs open</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-sm text-center">
                    <TrendingUp className="w-6 h-6 mx-auto text-purple-600 mb-1" />
                    <p className="text-sm font-semibold text-purple-700">
                      {stats.in_progress} In Progress
                    </p>
                    <p className="text-xs text-purple-600">Currently being fixed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Bug Detail/Update Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-red-600" />
              Bug Details
            </DialogTitle>
          </DialogHeader>
          {selectedBug && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-sm space-y-3">
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-lg">{selectedBug.title}</h3>
                  <div className="flex gap-2">
                    <span className={`status-badge ${getSeverityColor(selectedBug.severity)}`}>{selectedBug.severity}</span>
                    <span className={`status-badge ${getStatusColor(selectedBug.status)}`}>{selectedBug.status}</span>
                  </div>
                </div>
                <p className="text-slate-700">{selectedBug.description}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Reported by:</span>
                    <span className="ml-2 font-medium">{selectedBug.reported_by_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Category:</span>
                    <span className="ml-2">{selectedBug.category}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Created:</span>
                    <span className="ml-2">{new Date(selectedBug.created_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Priority:</span>
                    <span className="ml-2 font-mono">{selectedBug.priority}</span>
                  </div>
                </div>
                {selectedBug.steps_to_reproduce && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Steps to Reproduce:</p>
                    <p className="text-sm bg-white p-2 rounded-sm whitespace-pre-wrap">{selectedBug.steps_to_reproduce}</p>
                  </div>
                )}
              </div>

              {/* Admin Update Form */}
              {hasRole(['Admin']) && (
                <form onSubmit={handleUpdate} className="space-y-4 border-t border-slate-200 pt-4">
                  <h4 className="font-semibold">Update Bug</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={updateData.status} onValueChange={(v) => setUpdateData({ ...updateData, status: v })}>
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={updateData.priority} onValueChange={(v) => setUpdateData({ ...updateData, priority: v })}>
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Assign To</Label>
                      <Select value={updateData.assigned_to || 'unassigned'} onValueChange={(v) => setUpdateData({ ...updateData, assigned_to: v === 'unassigned' ? '' : v })}>
                        <SelectTrigger className="rounded-sm">
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Estimated Hours</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={updateData.estimated_hours}
                        onChange={(e) => setUpdateData({ ...updateData, estimated_hours: e.target.value })}
                        placeholder="0"
                        className="rounded-sm mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Resolution Notes</Label>
                    <Textarea
                      value={updateData.resolution_notes}
                      onChange={(e) => setUpdateData({ ...updateData, resolution_notes: e.target.value })}
                      placeholder="Notes about the fix or resolution..."
                      className="rounded-sm"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDetailDialogOpen(false)} className="rounded-sm">
                      Close
                    </Button>
                    <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                      Update Bug
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bug List */}
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="flex flex-wrap items-center gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
              <TabsList>
                <TabsTrigger value="all">All Bugs</TabsTrigger>
                <TabsTrigger value="my">My Reports</TabsTrigger>
                {hasRole(['Admin']) && <TabsTrigger value="assigned">Assigned to Me</TabsTrigger>}
              </TabsList>
            </Tabs>
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search bugs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 rounded-sm"
                data-testid="bug-search-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px] rounded-sm">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="spinner" />
            </div>
          ) : filteredBugs.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Reported By</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBugs.map((bug) => (
                  <TableRow 
                    key={bug.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => openDetail(bug)}
                    data-testid={`bug-row-${bug.id}`}
                  >
                    <TableCell className="font-medium max-w-xs truncate">{bug.title}</TableCell>
                    <TableCell>{bug.category}</TableCell>
                    <TableCell>
                      <span className={`status-badge ${getSeverityColor(bug.severity)}`}>{bug.severity}</span>
                    </TableCell>
                    <TableCell>
                      <span className={`status-badge ${getStatusColor(bug.status)}`}>{bug.status}</span>
                    </TableCell>
                    <TableCell className="mono">{bug.priority}</TableCell>
                    <TableCell className="text-slate-500">{bug.reported_by_name}</TableCell>
                    <TableCell className="text-slate-500">
                      {new Date(bug.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <Bug className="w-12 h-12 mb-3 text-slate-300" />
              <p>No bug reports found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BugReportsPage;
